import { useState, useEffect, useRef } from "react";

import { Button, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";

//Componentes
import JanelaFlutuante from "./JanelaFlutuante";
import {
  usePainelArquivo,
  ZOOM_TELA_MAXIMO,
  ZOOM_TELA_MINIMO,
} from "./PainelArquivo";

//Common
import { fn_getTituloArquivoPagamento } from "@/common/siig";

//Util
import { ICONES_BOTAO, ICONES_MENSAGEM } from "../utils/icones";
import {
  fn_getPosicaoLupa,
  fn_getRotacaoImagem,
  fn_girarImagem,
} from "../utils/util";
import { MENSAGENS_ERRO, showMensagemErro } from "../utils/mensagens";

//API
import { api_download } from "../services/api/api_others";

//Lupa da imagem: aumento e diâmetro (px)
const ZOOM_LUPA = 2.5;
const TAMANHO_LUPA = 180;

/*
 * Visualização do arquivo (PDF, imagem ou texto) com download e impressão.
 * Na imagem, a lupa amplia a parte em que o mouse passa (no PDF o próprio visualizador do navegador tem zoom).
 * Na imagem, o botão Girar gira 90 graus para a direita (ex: documento escaneado de lado). O giro vale só na visualização
 * (uma cópia girada gerada no navegador): o arquivo do servidor e o download continuam como foram enviados.
 * A lupa e a impressão usam a imagem girada. Ao trocar de arquivo, volta para a posição original.
 * flutuante: abre numa janela que pode ser movida e redimensionada, sem bloquear a tela que abriu
 * (ex: posse, para conferir o documento enquanto digita). Sem essa opção, abre no modal como antes.
 * flutuanteLateral: com flutuante, a janela abre encostada no canto direito e com toda a altura do navegador
 * (na tela com o painel do arquivo é sempre assim, inclusive para a forma de pagamento e o doc. complementar).
 * Painel do arquivo (componentes/PainelArquivo.jsx): na tela com PainelArquivoProvider (ex: posse), a janela flutuante
 * ganha o botão Fixar, que leva o arquivo para o lado esquerdo da tela; fixado, o arquivo aberto vai direto para o painel.
 * painel: o arquivo mostrado no próprio painel (usado pelo PainelArquivoLayout), com o botão Soltar.
 * solicitacao: (opcional) solicitação do arquivo do pagamento: com o tipo e a quantidade de pagamentos, o título
 * mostra "/qtde" no recorrente e no parcelado. Título do arquivo do pagamento: ver fn_getTituloArquivoPagamento.
 */
function ModalViewArquivo({
  pastaArquivo,
  nomeArquivo,
  isOpen,
  toggleModal,
  flutuante = false,
  flutuanteLateral = false,
  painel = false,
  solicitacao = null,
}) {
  //Painel do arquivo da tela (null fora de uma tela com PainelArquivoProvider)
  const painelArquivo = usePainelArquivo();
  const permiteFixar = flutuante && !painel && painelArquivo != null;

  //Identificação desta janela no painel
  const idRef = useRef(Math.random().toString(36).substring(2, 10));

  const isImage = /\.(jpg|jpeg|png|gif|jfif)$/i.test(nomeArquivo);
  const isImagemJpeg = /\.(jpg|jpeg|jfif)$/i.test(nomeArquivo);
  const isPdf = /\.pdf$/i.test(nomeArquivo);
  const isTxt = /\.txt$/i.test(nomeArquivo);

  //### USE STATE ###
  //Arquivo carregado e arquivo com erro guardam o nome do arquivo: na troca de arquivo com a janela aberta,
  //o endereço e o erro de um arquivo não são usados no outro
  const [arquivoCarregado, setArquivoCarregado] = useState({
    nome: null,
    url: null,
  });
  const [nomeArquivoErro, setNomeArquivoErro] = useState(null);
  const [conteudoTxt, setConteudoTxt] = useState("");

  //Aberto com o arquivo fixado: vai para o painel (até ser enviado, a janela flutuante não aparece)
  const [aguardaPainel, setAguardaPainel] = useState(
    () => permiteFixar && painelArquivo.isFixado,
  );

  //### USE STATE ###
  const [carregandoImagem, setCarregandoImagem] = useState(true);
  const [isLupaAtiva, setIsLupaAtiva] = useState(false);
  const [posicaoLupa, setPosicaoLupa] = useState(null); //posição e parte ampliada da imagem na lupa

  //Giro da imagem (0, 90, 180 ou 270 graus) e a cópia girada (com a imagem e o giro de que ela foi gerada)
  const [rotacao, setRotacao] = useState(0);
  const [imagemGirada, setImagemGirada] = useState({
    origem: null,
    rotacao: 0,
    url: null,
  });

  //### VARIÁVEIS AUXILIARES ###
  //Endereço e erro só do arquivo que está na tela
  const fileBlobUrl =
    arquivoCarregado.nome == nomeArquivo ? arquivoCarregado.url : null;
  const erroCarregamento = nomeArquivoErro == nomeArquivo;

  //Imagem apresentada: a original ou, girada, a cópia gerada para o giro atual (null enquanto gera a cópia)
  const urlImagem =
    rotacao == 0
      ? fileBlobUrl
      : imagemGirada.origem == fileBlobUrl && imagemGirada.rotacao == rotacao
        ? imagemGirada.url
        : null;

  //O arquivo desta janela está no painel: a janela não aparece (o painel mostra o arquivo)
  const isNoPainel =
    permiteFixar &&
    painelArquivo.isFixado &&
    (aguardaPainel || painelArquivo.arquivo?.id == idRef.current);

  //Arquivo desta janela para o painel (fechar: a tela que abriu fecha a janela)
  //Título: o tipo e o pagamento do arquivo (ex: "DOCUMENTO - Solicitação 500 pag 1/3"); fora do padrão, o nome do arquivo
  const tituloArquivoPagamento = fn_getTituloArquivoPagamento(nomeArquivo, {
    tipoPagamento: solicitacao?.tipo_pagamento,
    qtdePagamentos: solicitacao?.qtde_pagamentos,
  });
  const titulo = tituloArquivoPagamento || nomeArquivo;
  //No painel fixo, só o tipo do arquivo (o título da tela, com a solicitação, fica logo acima)
  const tituloPainel =
    fn_getTituloArquivoPagamento(nomeArquivo, { somenteTipo: true }) ||
    nomeArquivo;

  const getArquivoPainel = () => ({
    id: idRef.current,
    pastaArquivo,
    nomeArquivo,
    solicitacao,
    fechar: toggleModal,
  });

  //### USE EFFECT ###
  //Arquivo fixado: envia o arquivo aberto para o painel (e o arquivo trocado com a janela no painel)
  useEffect(() => {
    if (!permiteFixar) return;

    if (aguardaPainel) {
      painelArquivo.abrirNoPainel(getArquivoPainel());
      setAguardaPainel(false);
    } else if (
      painelArquivo.isFixado &&
      painelArquivo.arquivo?.id == idRef.current &&
      (painelArquivo.arquivo.nomeArquivo != nomeArquivo ||
        painelArquivo.arquivo.pastaArquivo != pastaArquivo)
    ) {
      painelArquivo.abrirNoPainel(getArquivoPainel());
    }
  }, [aguardaPainel, pastaArquivo, nomeArquivo]);

  //Janela flutuante aberta (fora do painel): na tela com uma janela só, fecha a janela do arquivo anterior
  useEffect(() => {
    if (!permiteFixar) return;

    if (isNoPainel) {
      painelArquivo.fecharJanela(idRef.current);
    } else {
      painelArquivo.abrirJanela(getArquivoPainel());
    }
  }, [isNoPainel, pastaArquivo, nomeArquivo]);

  //Janela fechada
  useEffect(() => {
    return () => painelArquivo?.fecharJanela(idRef.current);
  }, []);

  //Carrega o arquivo. Com a janela aberta, a tela pode trocar o arquivo (ex: janela flutuante): a cada troca
  //limpa o arquivo anterior e o erro, descarta o endereço do arquivo anterior e ignora a resposta atrasada dele
  useEffect(() => {
    //Arquivo no painel: quem carrega é o painel
    if (isNoPainel) return;

    let isAtual = true; //false depois que o arquivo foi trocado ou a tela foi fechada
    let urlArquivo = null;

    setNomeArquivoErro(null);
    setConteudoTxt("");
    setPosicaoLupa(null);
    setRotacao(0); //o arquivo novo abre na posição original

    async function carregarArquivo() {
      try {
        const url = await api_download(
          pastaArquivo,
          nomeArquivo,
          "S", //preview
          false, //mensagem
        );

        //Resposta de um arquivo que já foi trocado: descarta
        if (!isAtual) {
          if (url) {
            URL.revokeObjectURL(url);
          }
          return;
        }

        if (!url) {
          setNomeArquivoErro(nomeArquivo);
          return;
        }

        urlArquivo = url;
        setArquivoCarregado({ nome: nomeArquivo, url: url });

        if (isTxt) {
          const response = await fetch(url);
          const texto = await response.text();

          if (isAtual) {
            setConteudoTxt(texto);
          }
        }
      } catch (err) {
        if (isAtual) {
          setNomeArquivoErro(nomeArquivo);
        }
      }
    }

    carregarArquivo();

    return () => {
      isAtual = false;

      if (urlArquivo) {
        URL.revokeObjectURL(urlArquivo);
      }
    };
  }, [pastaArquivo, nomeArquivo, isTxt, isNoPainel]);

  //Imagem girada: gera a cópia girada a partir da imagem original. A cópia anterior é liberada ao girar de novo,
  //ao trocar de arquivo e ao fechar a janela
  useEffect(() => {
    if (!isImage || !fileBlobUrl || rotacao == 0) return;

    let isAtual = true;
    let urlGirada = null;

    fn_girarImagem(
      fileBlobUrl,
      rotacao,
      isImagemJpeg ? "image/jpeg" : "image/png",
    )
      .then((url) => {
        if (!isAtual) {
          URL.revokeObjectURL(url);
          return;
        }

        urlGirada = url;
        setImagemGirada({ origem: fileBlobUrl, rotacao, url });
      })
      .catch(() => {
        if (isAtual) {
          setRotacao(0);
          showMensagemErro(MENSAGENS_ERRO.TENTATIVA_OPERACAO, "girar a imagem");
        }
      });

    return () => {
      isAtual = false;

      if (urlGirada) {
        URL.revokeObjectURL(urlGirada);
      }
    };
  }, [fileBlobUrl, rotacao, isImage, isImagemJpeg]);

  //Controle do carregamento da imagem (também ao girar: a imagem girada é outra)
  useEffect(() => {
    if (isImage) {
      setCarregandoImagem(true);
    }
  }, [urlImagem, isImage]);

  //### EVENTOS ###
  //OnClick - Botão Download
  async function handleBotaoDownload() {
    await api_download(pastaArquivo, nomeArquivo);
  }

  //OnClick - Botão Lupa (liga e desliga)
  function handleBotaoLupa() {
    setIsLupaAtiva((prev) => !prev);
    setPosicaoLupa(null);
  }

  //OnClick - Botão Girar: gira 90 graus para a direita
  function handleBotaoGirar() {
    setRotacao((prev) => fn_getRotacaoImagem(prev, 1));
    setPosicaoLupa(null);
  }

  //OnMouseMove - Imagem: posiciona a lupa no mouse
  function handleMouseMoveImagem(event) {
    if (isLupaAtiva) {
      const rect = event.currentTarget.getBoundingClientRect();

      setPosicaoLupa(
        fn_getPosicaoLupa({
          mouseX: event.clientX - rect.left,
          mouseY: event.clientY - rect.top,
          largura: rect.width,
          altura: rect.height,
          tamanhoLupa: TAMANHO_LUPA,
          zoom: ZOOM_LUPA,
        }),
      );
    }
  }

  //OnClick - Botão Imprimir
  function handleBotaoImprimir() {
    //Imagem
    if (isImage) {
      const printWindow = window.open("", "_blank");

      const html = `
        <html>
          <head>
            <title>Imprimir</title>
            <style>
              body { margin: 0; text-align: center; }
              img {
                max-width: 100%;
                max-height: 100vh;
                object-fit: contain;
              }
            </style>
          </head>
          <body>
            <img src="${urlImagem}" />
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };

      return;
    }

    //PDF
    if (isPdf) {
      const iframe = document.querySelector("#preview-iframe");
      iframe?.contentWindow?.print();
      return;
    }

    //TXT
    if (isTxt) {
      const printWindow = window.open("", "_blank");

      printWindow.document.write(`
    <html>
      <head>
        <title>Imprimir</title>
        <style>
          body {
            font-family: monospace;
            white-space: pre-wrap;
            padding: 20px;
          }
        </style>
      </head>
      <body>${conteudoTxt}</body>
    </html>
  `);

      printWindow.document.close();

      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };

      return;
    }
  }

  //Botão da lupa (só para imagem)
  function getBotaoLupa(color, className) {
    return (
      isImage && (
        <Button
          name="btnLupa"
          size="sm"
          color={color}
          title={isLupaAtiva ? "Desligar lupa" : "Lupa"}
          onClick={handleBotaoLupa}
          active={isLupaAtiva}
          className={`${className || ""} ${isLupaAtiva ? "botao-ligado" : ""}`}
          disabled={erroCarregamento || carregandoImagem}
        >
          {ICONES_BOTAO.LUPA}
        </Button>
      )
    );
  }

  //Botão de girar a imagem (só para imagem): gira 90 graus para a direita, como o leitor de PDF do navegador
  function getBotaoGirar(color, className) {
    return (
      isImage && (
        <Button
          name="btnGirar"
          size="sm"
          color={color}
          title="Girar"
          onClick={handleBotaoGirar}
          className={className}
          disabled={erroCarregamento || carregandoImagem}
        >
          {ICONES_BOTAO.GIRAR}
        </Button>
      )
    );
  }

  //Botões da barra (lupa, girar, download, imprimir e fechar)
  const botoesBarra = (
    <div>
      {getBotaoLupa("primary", "me-1")}
      {getBotaoGirar("primary", "me-1")}

      <Button
        name="btnDownload"
        size="sm"
        color="primary"
        title="Download"
        onClick={handleBotaoDownload}
        className="me-1"
        disabled={erroCarregamento}
      >
        {ICONES_BOTAO.DOWNLOAD}
      </Button>

      <Button
        name="btnImprimir"
        size="sm"
        color="primary"
        title="Imprimir"
        onClick={handleBotaoImprimir}
        className="me-1"
        disabled={erroCarregamento || (!isImage && !isPdf && !isTxt)}
      >
        {ICONES_BOTAO.IMPRIMIR}
      </Button>

      <Button name="btnFechar" size="sm" title="Fechar" onClick={toggleModal}>
        {ICONES_BOTAO.FECHAR}
      </Button>
    </div>
  );

  //Conteúdo: o arquivo ou a mensagem de erro / sem pré-visualização
  const conteudo = erroCarregamento ? (
    <div className="text-center text-danger">
      {ICONES_MENSAGEM.ERRO} Documento não encontrado
      <br />
      {nomeArquivo}
    </div>
  ) : isImage ? (
    <div className="scroll-modal">
      {carregandoImagem && (
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ minHeight: "300px" }}
        >
          <Spinner color="primary" />
          <div className="mt-2">Carregando imagem...</div>
        </div>
      )}

      <div className={`lupa-imagem ${isLupaAtiva ? "lupa-ativa" : ""}`}>
        {
          //Só apresenta a imagem com o endereço dela. Cada endereço tem a sua imagem (key): o erro de uma
          //imagem que já saiu da tela (troca de arquivo) não chega a ser marcado no arquivo novo
          urlImagem && (
            <img
              key={urlImagem}
              src={urlImagem}
              onLoad={() => setCarregandoImagem(false)}
              onError={() => {
                setCarregandoImagem(false);
                setNomeArquivoErro(nomeArquivo);
              }}
              onMouseMove={handleMouseMoveImagem}
              onMouseLeave={() => setPosicaoLupa(null)}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain", //mantém proporção sem cortar
              }}
            />
          )
        }

        {isLupaAtiva && posicaoLupa && (
          <div
            className="lupa-lente"
            style={{
              width: TAMANHO_LUPA,
              height: TAMANHO_LUPA,
              left: posicaoLupa.left,
              top: posicaoLupa.top,
              backgroundImage: `url(${urlImagem})`,
              backgroundSize: posicaoLupa.backgroundSize,
              backgroundPosition: posicaoLupa.backgroundPosition,
            }}
          />
        )}
      </div>
    </div>
  ) : isTxt ? (
    <div
      style={{
        maxHeight: "70vh",
        overflow: "auto",
        backgroundColor: "#f8f9fa",
        padding: "10px",
      }}
    >
      <pre
        style={{
          margin: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {conteudoTxt}
      </pre>
    </div>
  ) : isPdf ? (
    <iframe
      id="preview-iframe"
      //O visualizador de PDF do navegador abre com a lateral das páginas fechada
      //(navpanes=0 no Chrome/Edge e pagemode=none no Firefox; cada navegador ignora o parâmetro que não conhece)
      src={fileBlobUrl ? `${fileBlobUrl}#navpanes=0&pagemode=none` : undefined}
      style={{ width: "100%", height: "100%", border: "none" }}
    />
  ) : (
    <div className="text-center">
      <p>
        <span className="text-warning me-2">{ICONES_MENSAGEM.AVISO}</span>A
        extensão deste arquivo não permite pré-visualização no navegador.
      </p>
      <p>
        Se desejar, realize o download do arquivo para visualizar ou imprimir.
      </p>
    </div>
  );

  //Botões de download e imprimir da barra da janela flutuante e do painel
  const botoesArquivo = (
    <>
      {getBotaoLupa("light")}
      {getBotaoGirar("light")}

      <Button
        name="btnDownload"
        size="sm"
        color="light"
        title="Download"
        onClick={handleBotaoDownload}
        disabled={erroCarregamento}
      >
        {ICONES_BOTAO.DOWNLOAD}
      </Button>

      <Button
        name="btnImprimir"
        size="sm"
        color="light"
        title="Imprimir"
        onClick={handleBotaoImprimir}
        disabled={erroCarregamento || (!isImage && !isPdf && !isTxt)}
      >
        {ICONES_BOTAO.IMPRIMIR}
      </Button>
    </>
  );

  //Arquivo no painel fixo da tela (PainelArquivoLayout): com o botão Soltar
  if (painel) {
    return (
      <div className="painel-arquivo-visualizador">
        <div className="janela-flutuante-cabecalho">
          <span className="janela-flutuante-titulo" title={nomeArquivo}>
            {tituloPainel}
          </span>

          <div className="d-flex align-items-center gap-1">
            {botoesArquivo}

            {/* Zoom da tela ao lado do painel */}
            {painelArquivo && (
              <div className="d-flex align-items-center gap-1 ms-2 me-2">
                <Button
                  name="btnZoomMenos"
                  size="sm"
                  color="light"
                  title="Diminuir o zoom da tela"
                  disabled={painelArquivo.zoomTela <= ZOOM_TELA_MINIMO}
                  onClick={() => painelArquivo.alterarZoomTela(false)}
                >
                  −
                </Button>

                <span
                  className="texto-menor text-nowrap"
                  title="Zoom da tela ao lado do documento"
                >
                  Tela {Math.round(painelArquivo.zoomTela * 100)}%
                </span>

                <Button
                  name="btnZoomMais"
                  size="sm"
                  color="light"
                  title="Aumentar o zoom da tela"
                  disabled={painelArquivo.zoomTela >= ZOOM_TELA_MAXIMO}
                  onClick={() => painelArquivo.alterarZoomTela(true)}
                >
                  +
                </Button>
              </div>
            )}

            <Button
              name="btnSoltar"
              size="sm"
              color="light"
              title="Soltar (volta para a janela flutuante)"
              className="botao-ligado"
              onClick={() => painelArquivo?.soltar()}
            >
              {ICONES_BOTAO.SOLTAR}
            </Button>

            <Button
              name="btnFechar"
              size="sm"
              title="Fechar"
              onClick={toggleModal}
            >
              {ICONES_BOTAO.FECHAR}
            </Button>
          </div>
        </div>

        <div className="janela-flutuante-corpo">{conteudo}</div>
      </div>
    );
  }

  //Arquivo desta janela no painel: a janela flutuante não aparece
  if (isNoPainel) {
    return null;
  }

  //Janela flutuante: não bloqueia a tela que abriu (o botão fechar fica na própria janela)
  if (flutuante) {
    return (
      <JanelaFlutuante
        titulo={titulo}
        dicaTitulo={tituloArquivoPagamento ? nomeArquivo : undefined}
        onFechar={toggleModal}
        //Na tela com o painel do arquivo (posse, conferência), toda janela abre encostada à direita e com a altura toda
        lateral={flutuanteLateral || permiteFixar}
        botoes={
          <>
            {botoesArquivo}

            {permiteFixar && (
              <Button
                name="btnFixar"
                size="sm"
                color="light"
                title="Fixar ao lado da tela"
                onClick={() => painelArquivo.fixar(getArquivoPainel())}
              >
                {ICONES_BOTAO.FIXAR}
              </Button>
            )}
          </>
        }
      >
        {conteudo}
      </JanelaFlutuante>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggleModal}
      backdrop="static"
      size={isPdf ? "xl" : "md"}
      className={isPdf ? "modal-fullscreen" : ""}
    >
      <ModalHeader className="d-flex align-items-center justify-content-end">
        <div className="d-flex align-items-center gap-3">
          {tituloArquivoPagamento && (
            <span className="texto-menor fw-bold" title={nomeArquivo}>
              {tituloArquivoPagamento}
            </span>
          )}
          {botoesBarra}
        </div>
      </ModalHeader>

      <ModalBody>{conteudo}</ModalBody>
    </Modal>
  );
}

export default ModalViewArquivo;
